import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateLocale, monthNames, weekdayNames } from '../../i18n/i18n';
import { Modal } from 'react-bootstrap';
import Skeleton from '../../components/skeleton';
import { getCalendarData } from '../../services/calendar';
import { onQuerySuccess } from '../../utils/queryResponse';
import { useQuery } from 'react-query';
import { toLocalDateString } from '../../utils/helper';
import SetTempEditOrderModal from '../../components/modals/orderEditTemp';
import { getRemindersMonthly } from '../../services/reminder';
import SetReminderModal from './reminderSetModal';
import SetOrderModal from '../../components/modals/orderSet';
import { getStatusLabel, getStatusMeta, getStatusPhase, normalizeStatus, JOB_STATUS, JOB_STATUS_ORDER } from '../../constants/jobStatus';

/* Every lifecycle stage (in order) plus Cancelled — same statuses shown on the job-status stepper */
const LEGEND_STATUSES = [...JOB_STATUS_ORDER, JOB_STATUS.CANCELLED];
const TONE_VAR = { grey: 'var(--wf-grey)', amber: 'var(--wf-amber)', green: 'var(--wf-green)', cancelled: 'var(--wf-cancelled)' };

const pageStyles = `
  .sbr .rail-head{display:flex;align-items:baseline;gap:8px;margin:0 0 12px}
  .sbr .rail-head .dd{font-size:17px;font-weight:800;letter-spacing:-.4px;color:var(--ink)}
  .sbr .rail-head .cn{margin-left:auto;font-size:11.5px;font-weight:800;color:var(--brand-ink);
    background:var(--brand-50);border:1px solid var(--brand-100);border-radius:999px;padding:3px 10px}
  .sbr .cal-legend{display:flex;flex-direction:column;gap:9px}
  .sbr .cal-legend .lr{display:flex;align-items:center;gap:9px;font-size:12.5px;font-weight:600;color:var(--text)}
  .sbr .cal-legend .lr i{width:11px;height:11px;border-radius:3px;flex-shrink:0}
  .sbr .cal-legend .lr .n{margin-left:auto;font-size:11.5px;font-weight:800;color:var(--muted);font-variant-numeric:tabular-nums}
  .sbr .day-total{display:flex;align-items:center;justify-content:space-between;margin-top:13px;
    padding-top:13px;border-top:1px solid var(--line);font-size:12.5px;font-weight:700;color:var(--muted)}
  .sbr .day-total b{font-size:16px;font-weight:800;color:var(--ink);font-variant-numeric:tabular-nums}
  .sbr .djob-rail{display:flex;flex-direction:column;gap:9px}
`;

/* Colors mirror the workflow tones in constants/jobStatus.js (grey/amber/green/cancelled) */
const TONE_TO_CLS    = { grey: 's-grey', amber: 's-amber', green: 's-green', cancelled: 's-cancelled' };
const TONE_TO_CHIP   = { grey: 'c-grey', amber: 'c-amber', green: 'c-green', cancelled: 'c-cancelled' };
const TONE_COLORS = {
    grey:      { bg: '#64748B', light: '#F1F5F9', text: '#fff' },
    amber:     { bg: '#D97706', light: '#FEF3C7', text: '#fff' },
    green:     { bg: '#16A34A', light: '#DCFCE7', text: '#fff' },
    cancelled: { bg: '#DC2626', light: '#FEF2F2', text: '#fff' },
}
const REMINDER_COLOR = { bg: '#0284C7', light: '#E0F2FE', text: '#fff' }

const getEventColors = (status, kind) => {
    if (kind === 'reminder') return REMINDER_COLOR
    return TONE_COLORS[getStatusMeta(status).tone]
}

/* Flatten a day cell's jobs + reminders into one ordered event list */
const buildDayEvents = (day) => [
    ...(day?.jobs      || []).map(j => ({ ...j, _kind: 'job'      })),
    ...(day?.reminders || []).map(r => ({ ...r, _kind: 'reminder' })),
]

/* Map job status to djob accent class */
const getDjobClass = (status, kind) => {
    if (kind === 'reminder') return 's-delivery'
    return TONE_TO_CLS[getStatusMeta(status).tone]
}

/* Map job status to cchip class */
const getChipClass = (status, kind) => {
    if (kind === 'reminder') return 'c-delivery'
    return TONE_TO_CHIP[getStatusMeta(status).tone]
}

const CalendarPage = () => {
    const { t } = useTranslation();
    // Month / weekday names in the active language — built on every render
    // (not at module level) so they follow a language switch.
    const months    = monthNames()
    const days      = weekdayNames('short')
    const daysFull  = weekdayNames()
    const daysShort = weekdayNames('short')
    const [editJobModal,      setEditJobModal]      = useState(false)
    const [reminderModal,     setReminderModal]     = useState(false)
    const [jobModal,          setJobModal]          = useState(false)
    const [todayDate]                               = useState(new Date())
    const [selectedMonth,     setSelectedMonth]     = useState('')
    const [selectedYear,      setSelectedYear]      = useState('')
    const [selectedDay,       setSelectedDay]       = useState(() => new Date().getDate())
    const [viewMode,          setViewMode]          = useState('month')
    const [startDate,         setStartDate]         = useState()
    const [lastDate,          setLastDate]          = useState()
    const [calendarData,      setCalendarData]      = useState([])
    const [reminders,         setReminders]         = useState([])
    const [selectedJobId,     setSelectedJobId]     = useState('')
    const [selectedReminder,  setSelectedReminder]  = useState({})
    const [jobPickupDateTime, setJobPickupDateTime] = useState({})
    const [dayModal,          setDayModal]          = useState(null)

    const { data: dbCalendar, isFetching: isFetchingCalendarData } = useQuery(
        ['calendar-data', selectedMonth, selectedYear],
        () => getCalendarData(selectedMonth + 1, selectedYear)
    )
    const { data: remindersData } = useQuery(
        ['reminders', 'monthly', selectedMonth, selectedYear],
        () => getRemindersMonthly(selectedMonth + 1, selectedYear)
    )

    useEffect(() => { onQuerySuccess(dbCalendar,    r => setCalendarData(r)) }, [dbCalendar])
    useEffect(() => { onQuerySuccess(remindersData, r => setReminders(r))    }, [remindersData])

    useEffect(() => {
        const year  = todayDate.getFullYear()
        const month = todayDate.getMonth()
        setSelectedYear(year)
        setSelectedMonth(month)
        setStartDate(new Date(year, month, 1))
        setLastDate(new Date(new Date(year, month + 1, 1) - 1))
    }, [todayDate])

    const getDataOnDate = useCallback((dataArray, dateNumber) =>
        dataArray.filter(d =>
            selectedYear  === new Date(d?.date).getFullYear()  &&
            selectedMonth === new Date(d?.date).getMonth()     &&
            dateNumber    === new Date(d?.date).getDate()
        ), [selectedYear, selectedMonth])

    const dateArray = useMemo(() => {
        const weeksArray = []
        const lastDate   = new Date(new Date(selectedYear, selectedMonth + 1, 1) - 1).getDate()
        let index        = new Date(selectedYear, selectedMonth, 1).getDay()
        let dateNumber   = 1

        const makeDay = (n) => {
            const jobs = getDataOnDate(calendarData, n).sort((a, b) => new Date(a.date) - new Date(b.date))
            return { dateNumber: n, jobs, reminders: getDataOnDate(reminders, n) }
        }

        if (index !== 0) {
            const row = new Array(7)
            for (let i = 0; i < 7; i++) row[i] = i < index ? {} : makeDay(dateNumber++)
            index = 0
            weeksArray.push(row)
        }
        while (dateNumber <= lastDate) {
            const row = new Array(7)
            for (let i = 0; i < 7; i++) {
                row[i] = dateNumber > lastDate ? {} : makeDay(dateNumber++)
            }
            weeksArray.push(row)
        }
        return weeksArray
    }, [selectedYear, selectedMonth, calendarData, getDataOnDate, reminders])

    const handlePreviousMonth = () => {
        if (selectedMonth === 0) {
            const year = selectedYear - 1
            setStartDate(new Date(year, 11, 1))
            setLastDate(new Date(new Date(year, 12, 1) - 1))
            setSelectedMonth(11); setSelectedYear(year)
        } else {
            setStartDate(new Date(selectedYear, selectedMonth - 1, 1))
            setLastDate(new Date(new Date(selectedYear, selectedMonth, 1) - 1))
            setSelectedMonth(selectedMonth - 1)
        }
        setCalendarData([])
        setSelectedDay(1)
    }

    const handleNextMonth = () => {
        if (selectedMonth === 11) {
            const year = selectedYear + 1
            setStartDate(new Date(year, 0, 1))
            setLastDate(new Date(new Date(year, 1, 1) - 1))
            setSelectedMonth(0); setSelectedYear(year)
        } else {
            const month = selectedMonth + 1
            setStartDate(new Date(selectedYear, month, 1))
            setLastDate(new Date(new Date(selectedYear, month + 1, 1) - 1))
            setSelectedMonth(month)
        }
        setCalendarData([])
        setSelectedDay(1)
    }

    const handleGoToToday = () => {
        const year  = todayDate.getFullYear()
        const month = todayDate.getMonth()
        // Only blank the data when the month actually changes. The data query is
        // keyed on [month, year]; if it's unchanged react-query won't refetch, so
        // clearing here would leave the day's jobs permanently empty (they vanish).
        if (month !== selectedMonth || year !== selectedYear) setCalendarData([])
        setSelectedYear(year); setSelectedMonth(month)
        setStartDate(new Date(year, month, 1))
        setLastDate(new Date(new Date(year, month + 1, 1) - 1))
        setSelectedDay(todayDate.getDate())
    }

    /* Week navigation — move startDate/endDate by 7 days, crossing month boundary if needed */
    const handlePreviousWeek = () => {
        const cur = new Date(selectedYear, selectedMonth, selectedDay)
        const prev = new Date(cur.getTime() - 7 * 86400000)
        // only blank the data when we cross into a different month (see handleGoToToday)
        if (prev.getMonth() !== selectedMonth || prev.getFullYear() !== selectedYear) setCalendarData([])
        setSelectedYear(prev.getFullYear())
        setSelectedMonth(prev.getMonth())
        setSelectedDay(prev.getDate())
        setStartDate(new Date(prev.getFullYear(), prev.getMonth(), 1))
        setLastDate(new Date(new Date(prev.getFullYear(), prev.getMonth() + 1, 1) - 1))
    }

    const handleNextWeek = () => {
        const cur = new Date(selectedYear, selectedMonth, selectedDay)
        const next = new Date(cur.getTime() + 7 * 86400000)
        // only blank the data when we cross into a different month (see handleGoToToday)
        if (next.getMonth() !== selectedMonth || next.getFullYear() !== selectedYear) setCalendarData([])
        setSelectedYear(next.getFullYear())
        setSelectedMonth(next.getMonth())
        setSelectedDay(next.getDate())
        setStartDate(new Date(next.getFullYear(), next.getMonth(), 1))
        setLastDate(new Date(new Date(next.getFullYear(), next.getMonth() + 1, 1) - 1))
    }

    /* The row in dateArray that contains selectedDay */
    const selectedWeekRow = useMemo(() =>
        dateArray.find(row => row.some(d => d.dateNumber === selectedDay)) || dateArray[0]
    , [dateArray, selectedDay])

    const isTodayDate = (n) =>
        selectedYear  === todayDate.getFullYear()  &&
        selectedMonth === todayDate.getMonth()     &&
        n             === todayDate.getDate()

    const handleSelectData  = (data)     => { setSelectedJobId(data.id); setEditJobModal(true) }
    const handleSetReminder = (reminder) => { setSelectedReminder(reminder); setReminderModal(true) }
    const handleSetJob      = (dt)       => { setJobPickupDateTime(dt); setJobModal(true) }

    /* Day-detail popup (reveals all events behind "+N more") */
    const openDayModal  = (day) => setDayModal({ dateNumber: day.dateNumber, events: buildDayEvents(day) })
    const closeDayModal = ()    => setDayModal(null)
    const dayModalDate  = dayModal ? new Date(selectedYear, selectedMonth, dayModal.dateNumber) : null

    const openJobFromDay      = (ev) => { closeDayModal(); handleSelectData(ev) }
    const openReminderFromDay = (ev) => { closeDayModal(); handleSetReminder(ev) }
    const addJobForDay        = ()   => { const d = dayModalDate; closeDayModal(); handleSetJob(d) }
    const addReminderForDay   = ()   => { const d = dayModalDate; closeDayModal(); handleSetReminder({ date: d }) }

    /* Flat array for single CSS grid */
    const flatDays = useMemo(() => dateArray.flat(), [dateArray])
    const numWeeks = dateArray.length

    /* Mobile week strip: real days of the month + auto-center the selected day */
    const monthDays = useMemo(() => flatDays.filter(d => d.dateNumber), [flatDays])
    const weekRef   = useRef(null)
    const selDayRef = useRef(null)
    useEffect(() => {
        const el = selDayRef.current, container = weekRef.current
        if (!el || !container) return
        const er = el.getBoundingClientRect(), cr = container.getBoundingClientRect()
        const delta = (er.left - cr.left) - (cr.width / 2) + (er.width / 2)
        container.scrollBy({ left: delta, behavior: 'smooth' })  // horizontal only — never moves page scroll
    }, [selectedDay, selectedMonth, selectedYear, monthDays])

    /* Right-rail: jobs for selectedDay */
    const selectedDayJobs = useMemo(() =>
        getDataOnDate(calendarData, selectedDay).sort((a, b) => new Date(a.date) - new Date(b.date))
    , [calendarData, selectedDay, getDataOnDate])

    const selectedDayReminders = useMemo(() =>
        getDataOnDate(reminders, selectedDay)
    , [reminders, selectedDay, getDataOnDate])

    const selectedDayEvents = [...selectedDayJobs.map(j => ({ ...j, _kind: 'job' })),
                               ...selectedDayReminders.map(r => ({ ...r, _kind: 'reminder' }))]
                              .sort((a, b) => new Date(a.date) - new Date(b.date))

    const dayTotal = selectedDayJobs.reduce((s, j) => s + parseFloat(j.price || 0), 0)

    const selectedDayDate = selectedDay && selectedYear && selectedMonth !== ''
        ? new Date(selectedYear, selectedMonth, selectedDay) : new Date()
    const selectedDayLabel = selectedDayDate.toLocaleDateString(dateLocale(), { weekday: 'short', day: 'numeric', month: 'long' })

    /* Legend counts for this month, one per lifecycle status */
    const legendCounts = useMemo(() => {
        const c = {}
        calendarData.forEach(j => { const s = normalizeStatus(j.status); c[s] = (c[s] || 0) + 1 })
        return c
    }, [calendarData])

    return (
        <div className="sbr">
            <style>{pageStyles}</style>
            <SetTempEditOrderModal display={editJobModal}  toggle={setEditJobModal}  jobId={selectedJobId} />
            <SetReminderModal      display={reminderModal} toggle={setReminderModal} reminder={selectedReminder} />
            <SetOrderModal         display={jobModal}      toggle={setJobModal}      pickupTime={jobPickupDateTime} />

            {/* Day detail modal — reveals every event behind "+N more" */}
            <Modal show={!!dayModal} onHide={closeDayModal} centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title style={{ width: '98%' }}>
                        <div className="sb-modal-head">
                            <span className="sb-modal-ic">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                                    <line x1="16" y1="2" x2="16" y2="6"/>
                                    <line x1="8" y1="2" x2="8" y2="6"/>
                                    <line x1="3" y1="10" x2="21" y2="10"/>
                                </svg>
                            </span>
                            <div className="sb-modal-htext">
                                <span className="sb-modal-eyebrow">{dayModalDate ? `${daysFull[dayModalDate.getDay()]} · ${months[selectedMonth]} ${selectedYear}` : ''}</span>
                                <span className="sb-modal-title">{dayModal ? `${dayModal.dateNumber} ${months[selectedMonth]}` : ''}</span>
                            </div>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {dayModal?.events?.length ? (
                        <div className="cal-day-list">
                            {dayModal.events.map((ev) => {
                                const c = getEventColors(ev.status, ev._kind)
                                return ev._kind === 'job' ? (
                                    <button key={`job-${ev.id}`} type="button" className="cal-day-ev"
                                        style={{ '--ev': c.bg }} onClick={() => openJobFromDay(ev)}>
                                        <span className="cal-day-ev-bar" />
                                        <span className="cal-day-ev-main">
                                            <span className="cal-day-ev-title">{ev.clientName}</span>
                                            <span className="cal-day-ev-sub">{ev.status ? getStatusLabel(ev.status) : (ev.type || t('calendar.job'))}</span>
                                        </span>
                                        <span className="cal-day-ev-time">{toLocalDateString(ev.date, "HH:mm")}</span>
                                    </button>
                                ) : (
                                    <button key={`rem-${ev.id}`} type="button" className="cal-day-ev"
                                        style={{ '--ev': c.bg }} onClick={() => openReminderFromDay(ev)}>
                                        <span className="cal-day-ev-bar" />
                                        <span className="cal-day-ev-main">
                                            <span className="cal-day-ev-title"><i className="fa-solid fa-clock me-2" style={{ fontSize: '11px', color: c.bg }} />{ev.title}</span>
                                            <span className="cal-day-ev-sub">{t('calendar.reminder')}</span>
                                        </span>
                                        <span className="cal-day-ev-time">{toLocalDateString(ev.date, "HH:mm")}</span>
                                    </button>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="sbr-empty">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2"/>
                                <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                            {t('calendar.nothingScheduledForDay')}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ justifyContent: 'flex-start', gap: '8px' }}>
                    <button type="button" className="cal-day-add" onClick={addJobForDay}>
                        <i className="fa-solid fa-hammer me-2" /> {t('calendar.addJob')}
                    </button>
                    <button type="button" className="cal-day-add" onClick={addReminderForDay}>
                        <i className="fa-solid fa-note-sticky me-2" /> {t('calendar.addReminder')}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Desktop header */}
            <div className="dhead sbr-d">
                <div>
                    <div className="ttl">{t('calendar.title')}</div>
                    <div className="sub">{calendarData.length > 0 ? t('calendar.jobsInMonth', { count: calendarData.length, month: months[selectedMonth] }) : `${months[selectedMonth]} ${selectedYear}`}</div>
                </div>
                <div className="act">
                    <button className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => handleSetReminder({ date: new Date() })}>
                        <svg className="ic" style={{ width:15, height:15 }} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                        </svg>
                        {t('calendar.addReminder')}
                    </button>
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleSetJob(new Date())}>
                        <svg className="ic" style={{ width:15, height:15 }} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        {t('calendar.newJob')}
                    </button>
                </div>
            </div>


{/* Mobile page header */}
            <div className="sbr-m" style={{ marginBottom: '14px' }}>
                <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.8px', color: 'var(--ink)' }}>{t('calendar.title')}</div>
                    <div style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 500, marginTop: '2px' }}>{months[selectedMonth]} {selectedYear} · {t('calendar.tapDayToSee')}</div>
                </div>
                {/* Create a job or a reminder for the currently selected day */}
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" style={{ flex: 1, justifyContent: 'center' }} onClick={() => handleSetJob(selectedDayDate)}>
                        <svg className="ic" style={{ width: '15px', height: '15px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"/>
                            <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        {t('calendar.newJob')}
                    </button>
                    <button className="btn-sbr btn-ghost-sbr btn-sm-sbr" style={{ flex: 1, justifyContent: 'center' }} onClick={() => handleSetReminder({ date: selectedDayDate })}>
                        <svg className="ic" style={{ width: '15px', height: '15px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                        </svg>
                        {t('calendar.reminder')}
                    </button>
                </div>
            </div>

            {/* Calendar + right rail */}
            <div className="cols sbr-d">

            {/* Calendar card */}
            <div className="calcard" style={{ padding: 0, overflow: 'hidden', margin: 0 }}>

                {/* Nav bar */}
                <div className="cal-bar" style={{ padding: '16px 20px 0' }}>
                    <div className="month num">
                        {viewMode === 'week' && selectedWeekRow
                            ? (() => {
                                const first = selectedWeekRow.find(d => d.dateNumber)
                                const last  = [...selectedWeekRow].reverse().find(d => d.dateNumber)
                                return `${first?.dateNumber}–${last?.dateNumber} ${months[selectedMonth]} ${selectedYear}`
                              })()
                            : `${months[selectedMonth]} ${selectedYear}`
                        }
                    </div>
                    <div className="navgrp">
                        <button aria-label={t('calendar.previous')} onClick={viewMode === 'week' ? handlePreviousWeek : handlePreviousMonth}>
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6"/>
                            </svg>
                        </button>
                        <button aria-label={t('common.next')} onClick={viewMode === 'week' ? handleNextWeek : handleNextMonth}>
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="9 18 15 12 9 6"/>
                            </svg>
                        </button>
                    </div>
                    <button className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={handleGoToToday} style={{ marginLeft: '4px' }}>
                        {t('calendar.today')}
                    </button>
                    <div className="toggle" style={{ marginLeft: 'auto' }}>
                        <span className={viewMode === 'month' ? 'on' : ''} onClick={() => setViewMode('month')} style={{ cursor: 'pointer' }}>{t('calendar.month')}</span>
                        <span className={viewMode === 'week'  ? 'on' : ''} onClick={() => setViewMode('week')}  style={{ cursor: 'pointer' }}>{t('calendar.week')}</span>
                    </div>
                </div>

                {/* Day-of-week headers */}
                <div className="cal-dow" style={{ margin: '16px 0 0' }}>
                    {days.map((day, i) => (
                        <div key={day} className={i === 0 || i === 6 ? 'we' : ''}>
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar grid */}
                {isFetchingCalendarData ? (
                    <div className="cal-grid" style={{ gridTemplateRows: 'minmax(106px, auto)' }}>
                        {Array.from({ length: 7 }).map((_, i) => (
                            <div key={i} className="cell">
                                <Skeleton width={24} height={24} radius="8px" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="cal-grid" style={{ gridTemplateRows: viewMode === 'week' ? 'minmax(200px, auto)' : `repeat(${numWeeks}, minmax(106px, auto))` }}>
                        {(viewMode === 'week' ? (selectedWeekRow || []) : flatDays).map((day, index) => {
                            const di         = index % 7
                            const isToday    = isTodayDate(day.dateNumber)
                            const isEmpty    = !day.dateNumber
                            const isWeekend  = di === 0 || di === 6

                            const allEvents     = buildDayEvents(day)
                            const chipLimit     = viewMode === 'week' ? allEvents.length : 3
                            const visibleEvents = allEvents.slice(0, chipLimit)
                            const extraCount    = allEvents.length - chipLimit

                            const isSelected = day.dateNumber === selectedDay

                            let cellClass = 'cell'
                            if (isWeekend) cellClass += ' we'
                            if (isEmpty)   cellClass += ' out'
                            if (isToday)   cellClass += ' today'
                            if (isSelected && !isEmpty) cellClass += ' sel'

                            return (
                                <div key={index} className={cellClass}
                                    onClick={() => !isEmpty && setSelectedDay(day.dateNumber)}>
                                    {!isEmpty && (
                                        <>
                                            {/* Date number + quick-add buttons */}
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                                                <div className="dn num">{day.dateNumber}</div>
                                                <span style={{ display: 'flex', gap: '1px' }}>
                                                    <button
                                                        title={t('calendar.addReminder')}
                                                        onClick={(e) => { e.stopPropagation(); handleSetReminder({ date: new Date(selectedYear, selectedMonth, day.dateNumber) }) }}
                                                        style={{ background: 'none', border: 'none', padding: '2px 3px', cursor: 'pointer', borderRadius: '5px', color: 'var(--faint)', lineHeight: 1 }}
                                                    >
                                                        <i className="fa-xs fa-solid fa-note-sticky" />
                                                    </button>
                                                    <button
                                                        title={t('calendar.addJob')}
                                                        onClick={(e) => { e.stopPropagation(); handleSetJob(new Date(selectedYear, selectedMonth, day.dateNumber)) }}
                                                        style={{ background: 'none', border: 'none', padding: '2px 3px', cursor: 'pointer', borderRadius: '5px', color: 'var(--faint)', lineHeight: 1 }}
                                                    >
                                                        <i className="fa-xs fa-solid fa-hammer" />
                                                    </button>
                                                </span>
                                            </div>

                                            {/* Event chips */}
                                            {visibleEvents.map((ev) => (
                                                ev._kind === 'job' ? (
                                                    <div
                                                        key={ev.id}
                                                        className={`cchip ${getChipClass(ev.status, ev._kind)}`}
                                                        onClick={(e) => { e.stopPropagation(); handleSelectData(ev) }}
                                                        title={`${ev.clientName} – ${toLocalDateString(ev.date, "HH:mm")}`}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <i />
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>
                                                            {toLocalDateString(ev.date, "HH:mm")} {ev.clientName}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div
                                                        key={ev.id}
                                                        className="cchip c-delivery"
                                                        onClick={(e) => { e.stopPropagation(); handleSetReminder(ev) }}
                                                        title={ev.title}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <i />
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>
                                                            {ev.title}
                                                        </span>
                                                    </div>
                                                )
                                            ))}

                                            {extraCount > 0 && (
                                                <span
                                                    className="more"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => openDayModal(day)}
                                                    title={t('common.viewAll')}
                                                >
                                                    {t('calendar.more', { count: extraCount })}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}

            </div>{/* end calcard */}

            {/* ── Right rail ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', margin: 0 }}>

                {/* Day agenda card */}
                <div className="card-sbr" style={{ margin: 0 }}>
                    <div className="rail-head">
                        <div className="dd">{selectedDayLabel}</div>
                        {selectedDayEvents.length > 0 && (
                            <span className="cn">{selectedDayEvents.length === 1 ? t('calendar.jobScheduled') : t('calendar.jobsScheduled', { count: selectedDayEvents.length })}</span>
                        )}
                    </div>

                    {selectedDayEvents.length === 0 ? (
                        <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--muted)', fontSize: '13px', fontStyle: 'italic' }}>
                            {t('calendar.nothingScheduled')}
                        </div>
                    ) : (
                        <div className="djob-rail">
                            {selectedDayEvents.map((ev, i) => {
                                const cls = getDjobClass(ev.status, ev._kind)
                                const time = toLocalDateString(ev.date, 'HH:mm')
                                const name = ev._kind === 'reminder' ? ev.title : ev.clientName
                                const addr = ev.address || ev.clientAddress || ''
                                const price = ev.price ? `${parseFloat(ev.price).toLocaleString('sr-RS', { minimumFractionDigits: 2 })} RSD` : null
                                return (
                                    <div key={`${ev._kind}-${ev.id}`} className={`djob ${cls}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => ev._kind === 'job' ? handleSelectData(ev) : handleSetReminder(ev)}>
                                        <div className="time">
                                            <div className="hm">{time}</div>
                                            <div className="dur">{ev._kind === 'reminder' ? t('calendar.note') : getStatusPhase(ev.status) === 'done' ? t('calendar.done') : '—'}</div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className="cust">{name || '—'}</div>
                                            {addr && (
                                                <div className="loc">
                                                    <svg className="ic" style={{ width: 13, height: 13 }} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                                                    </svg>
                                                    {addr.split(',').slice(0, 2).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                        {price && <div className="price">{price}</div>}
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {dayTotal > 0 && (
                        <div className="day-total">
                            <span>{t('calendar.dayTotal')}</span>
                            <b>{dayTotal.toLocaleString('sr-RS', { minimumFractionDigits: 2 })} RSD</b>
                        </div>
                    )}
                </div>

                {/* Legend card */}
                <div className="card-sbr" style={{ margin: 0 }}>
                    <div className="ch">
                        <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>
                        </svg>
                        <span className="t">{t('calendar.statusLegend')}</span>
                    </div>
                    <div className="cal-legend">
                        {LEGEND_STATUSES.map(status => {
                            const meta = getStatusMeta(status)
                            return (
                                <div key={status} className="lr">
                                    <i style={{ background: TONE_VAR[meta.tone] }}></i>
                                    {meta.label}
                                    <span className="n">{legendCounts[status] || 0}</span>
                                </div>
                            )
                        })}
                    </div>
                </div>

            </div>{/* end right rail */}

            </div>{/* end .cols */}

            {/* Mobile calendar — swipeable week strip + selected-day agenda */}
            <div className="sbr-m">
                {/* Month bar */}
                <div className="monthbar">
                    <span className="ml">{months[selectedMonth]}</span><span className="yr num">{selectedYear}</span>
                    <div className="navg" style={{ marginLeft: 'auto' }}>
                        <button className="mnav" aria-label={t('calendar.previousMonth')} onClick={handlePreviousMonth}>
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                        </button>
                        <button className="mnav" aria-label={t('calendar.nextMonth')} onClick={handleNextMonth}>
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                    </div>
                    <button className="mtoggle" onClick={handleGoToToday}>
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="9" y1="4" x2="9" y2="22"/></svg>
                        {t('calendar.today')}
                    </button>
                </div>

                {/* Horizontal week strip */}
                <div className="weekwrap">
                    <div className="week" ref={weekRef}>
                        {monthDays.map((day) => {
                            const events = buildDayEvents(day)
                            const dotCount = Math.min(events.length, 3)
                            const wd = daysShort[new Date(selectedYear, selectedMonth, day.dateNumber).getDay()]
                            let cls = 'dcell'
                            if (isTodayDate(day.dateNumber)) cls += ' today'
                            if (day.dateNumber === selectedDay) cls += ' sel'
                            else if (events.length === 0) cls += ' off'
                            return (
                                <button key={day.dateNumber} type="button" className={cls}
                                    ref={day.dateNumber === selectedDay ? selDayRef : null}
                                    onClick={() => setSelectedDay(day.dateNumber)}>
                                    <span className="wd">{wd}</span>
                                    <span className="dn2 num">{day.dateNumber}</span>
                                    <span className="dots">
                                        {Array.from({ length: dotCount }).map((_, k) => <i key={k} />)}
                                    </span>
                                    <span className="cnt num">{events.length || ''}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>

                {/* Selected-day summary */}
                <div className="agday">
                    <div className="dno">
                        <b className="num">{selectedDay}</b>
                        <small>{daysShort[selectedDayDate.getDay()]}</small>
                    </div>
                    <div className="tx">
                        <b>{selectedDayLabel}</b>
                        <span>{selectedDayEvents.length === 1 ? t('calendar.jobScheduled') : t('calendar.jobsScheduled', { count: selectedDayEvents.length })}</span>
                    </div>
                    {dayTotal > 0 && (
                        <div className="val">
                            <div className="v num">{dayTotal.toLocaleString('sr-RS', { minimumFractionDigits: 2 })} RSD</div>
                            <div className="l">{t('calendar.dayValue')}</div>
                        </div>
                    )}
                </div>

                {/* Selected-day agenda cards */}
                {selectedDayEvents.length === 0 ? (
                    <div className="sbr-empty" style={{ padding: '28px 14px' }}>
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        {t('calendar.nothingScheduledForDay')}
                    </div>
                ) : (
                    selectedDayEvents.map((ev) => {
                        const cls   = getDjobClass(ev.status, ev._kind)
                        const time  = toLocalDateString(ev.date, 'HH:mm')
                        const name  = ev._kind === 'reminder' ? ev.title : ev.clientName
                        const addr  = ev.address || ev.clientAddress || ''
                        const dur   = ev._kind === 'reminder' ? t('calendar.note') : getStatusPhase(ev.status) === 'done' ? t('calendar.done') : '—'
                        const price = ev._kind === 'job' && ev.price ? `${parseFloat(ev.price).toLocaleString('sr-RS', { minimumFractionDigits: 2 })} RSD` : null
                        const badgeCls = getChipClass(ev.status, ev._kind).replace('c-', 'b-')
                        const badgeLabel = ev._kind === 'reminder' ? t('calendar.reminder') : (ev.status ? getStatusLabel(ev.status) : (ev.type || t('calendar.today')))
                        return (
                            <div key={`${ev._kind}-${ev.id}`} className={`job ${cls}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => ev._kind === 'job' ? handleSelectData(ev) : handleSetReminder(ev)}>
                                <div className="time">
                                    <div className="hm num">{time}</div>
                                    <div className="dur num">{dur}</div>
                                </div>
                                <div className="body">
                                    <div className="top"><span className="cust">{name || '—'}</span></div>
                                    {addr && (
                                        <div className="loc">
                                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                            {addr.split(',').slice(0, 2).join(', ')}
                                        </div>
                                    )}
                                    <div className="foot">
                                        <span className={`badge-sbr ${badgeCls}`}>{badgeLabel}</span>
                                        {price && <span className="price num">{price}</span>}
                                    </div>
                                </div>
                                <svg className="chev ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ alignSelf: 'center' }}><polyline points="9 18 15 12 9 6"/></svg>
                            </div>
                        )
                    })
                )}
            </div>

        </div>
    )
}

export default CalendarPage;
